import React, { useState, useEffect } from "react";
import { Box, TextField, InputAdornment } from "@mui/material";
import { Search as SearchIcon } from "@mui/icons-material";

/**
 *
 * @param {Object} props
 * @param {string} props.placeholder - Placeholder text for the search field
 * @param {Function} props.onSearch - Function to be called when search is performed
 * @param {number} props.debounceTime - Time in ms to wait before triggering search (default: 500ms)
 * @param {string} props.initialValue - Initial value for the search field
 * @param {Object} props.sx - Additional styles to apply to the container
 * @param {Object} props.inputProps - Additional props for the TextField component
 *
 * @returns {JSX.Element} - CommonSearch component
 */
const CommonSearch = ({
  placeholder = "Search",
  onSearch,
  debounceTime = 500,
  initialValue = "",
  sx = {},
  inputProps = {},
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);

  // Debounce search to avoid unnecessary API calls
  useEffect(() => {
    const handler = setTimeout(() => {
      if (onSearch) {
        onSearch(searchTerm);
      }
    }, debounceTime);

    return () => {
      clearTimeout(handler);
    };
  }, [searchTerm, onSearch, debounceTime]);

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", ...sx }}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleSearchChange}
        sx={{ flexGrow: 1 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
          ...inputProps.InputProps,
        }}
        style={{
          border: "1px solid black",
          borderRadius: "8px",
          ...inputProps.style,
        }}
        {...inputProps}
      />
    </Box>
  );
};

export default CommonSearch;
